<template>
  <div style="height: 365px; margin-top: 20px" 
       role="region" 
       aria-labelledby="wheel-game-heading"
       @keydown.space.prevent="triggerSpin"
       @keydown.enter="triggerSpin"
       tabindex="0">
    
    <h3 id="wheel-game-heading" class="sr-only">{{ $t('Accessibility.WheelOfFortune.Title') }}</h3>
    
    <!-- Game instructions for screen readers -->
    <div class="sr-only" aria-live="polite" id="wheel-instructions">
      {{ gameInstructions }}
    </div>

    <!-- Current game state announcement -->
    <div class="sr-only" aria-live="polite" id="wheel-status">
      {{ gameStatus }}
    </div>

    <wheel
      :isDemo="isDemo"
      :inFrame="inFrame"
      :spinButtonBackgroundColor="data.spinButtonBackgroundColor"
      :spinButtonText="data.spinButtonText"
      :spinButtonTextColor="data.spinButtonTextColor"
      :spinItems="data.spinItems"
      :spinButtonFontFamily="data.spinButtonFontFamily"
      :spin-button-font-size="data.spinButtonFontSize"
      :segmentsFontFamily="data.segmentsFontFamily"
      :segmentsFontSize="data.segmentsFontSize"
      :segmentsStrokeWidth="data.segmentsStrokeWidth"
      :segmentsStrokeColor="data.segmentsStrokeColor"
      :id="data.id"
      :centerImageUrl="data.centerImageUrl"
      :centerBackgroundColor="data.centerBackgroundColor"
      :centerCircleSize="data.centerCircleSize"
      :centerCircleStrokeColor="data.centerCircleStrokeColor"
      :centerCircleStrokeWidth="data.centerCircleStrokeWidth"
      :wheelStrokeColor="data.wheelStrokeColor"
      :wheelStrokeWidth="data.wheelStrokeWidth"
      :wheelTextColor="data.wheelTextColor"
      :pegBackgroundColor="data.pegBackgroundColor"
      :segmentsImageSize="data.segmentsImageSize"
      :segmentsImageOffset="data.segmentsImageOffset"
      @onComplete="onComplete"
      @onStart="onStart"
      ref="wheelComponent"
      v-if="data"
      role="application"
      :aria-label="wheelAriaLabel">
    </wheel>

  </div>

</template>

<script>

import Wheel from './subcomponents/wheel_updated';

export default {
  props: {
    data: {},
    isDemo: false,
    inFrame: false,
  },
  components: {
    Wheel,
  },
  data() {
    return {
      completed: false,
      spinning: false,
      gameStatus: ''
    };
  },
  computed: {
    gameInstructions() {
      return this.$t('Accessibility.WheelOfFortune.Instructions');
    },
    wheelAriaLabel() {
      if (this.data && this.data.spinItems) {
        const segments = this.data.spinItems.map(item => item.text).join(', ');
        return this.$t('Accessibility.WheelOfFortune.AriaLabel', {
          segments: segments,
          buttonText: this.data.spinButtonText || this.$t('Accessibility.WheelOfFortune.Ready')
        });
      }
      return this.$t('Accessibility.WheelOfFortune.Title');
    }
  },
  mounted() {
    // Set initial game status
    this.gameStatus = this.$t('Accessibility.WheelOfFortune.Ready');
  },
  methods: {
    onComplete: function (event) {
      this.completed = true;
      this.spinning = false;
      
      // Announce result to screen readers
      if (event && event.text) {
        this.gameStatus = this.$t('Accessibility.WheelOfFortune.StoppedOn', { result: event.text });
      } else {
        this.gameStatus = this.$t('Accessibility.WheelOfFortune.Stopped');
      }
      
      // `this` inside methods points to the Vue instance
      this.$emit('end', event);
    },
    
    onStart: function (event) {
      this.spinning = true;
      this.gameStatus = this.$t('Accessibility.WheelOfFortune.Spinning');
      this.$emit('start', event);
    },
    
    triggerSpin() {
      // Keyboard accessibility - trigger spin via keyboard
      if (!this.spinning && !this.completed && this.$refs.wheelComponent) {
        // Try to trigger the wheel's spin method if available
        if (this.$refs.wheelComponent.spin) {
          this.$refs.wheelComponent.spin();
        } else if (this.$refs.wheelComponent.$el) {
          // Fallback: try to click the spin button
          const spinButton = this.$refs.wheelComponent.$el.querySelector('button, [role="button"]');
          if (spinButton) {
            spinButton.click();
          }
        }
      }
    }
  }
};

</script>

<style>
/* Screen reader only content */
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Remove focus outline from wheel container since focus is handled by inner wheel component */
div[role="region"][tabindex="0"]:focus {
  outline: none;
}
</style>
